import React, { useCallback, useEffect } from "react";
import Grid from "@material-ui/core/Grid";
import styled from "styled-components/macro";

import { useSelector } from "react-redux";
import { getCurrentRoundsList, getRounds, getSetting } from "selectors";
import { PenPicker } from "components/Form/FormikControls/PenPicker";
import { useFieldState } from "hooks";
import { PenTypes } from "constants/auctionPens";
import { withNamespace } from "components/Form/FormikControls";
import { Settings } from "constants/settings";
import { FilterComponentProps } from "components/Filter/types";
import { OptionToggler } from "components/OptionToggler";
import { ReactSelect } from "components/SearchableSelector";
import { AuctionPenSpanMessage } from "./PenMessage";

const ControlGroupTitle = styled.h2`
  margin: 0;
`;
const ControlGroupSubtitle = styled.p(
  ({ theme: { colors, fontWeights, fontSizes, space } }) => `
  color: ${colors.titleMedium};
  font-weight:${fontWeights.normal};
  font-size:${fontSizes.alpha}px;
  margin: ${space[0]}px ${space[0]}px ${space[1]}px ${space[0]}px;
`,
);

interface ControlGroupHeaderProps {
  title: string | React.ReactNode;
  subtitle: string | React.ReactNode | undefined;
}

function ControlGroupHeader(props: ControlGroupHeaderProps) {
  const { subtitle, title } = props;
  return (
    <header>
      <ControlGroupTitle>{title}</ControlGroupTitle>
      {subtitle && <ControlGroupSubtitle>{subtitle}</ControlGroupSubtitle>}
    </header>
  );
}

interface RoundSelectorProps {
  namespace: string;
  onAfterChanged?: (value: number) => void;
}

interface RoundOption {
  label: string;
  value: number;
}

function RoundSelector(props: RoundSelectorProps): React.JSX.Element {
  const { namespace: ns, onAfterChanged } = props;

  const globalRoundId: number = useSelector(getSetting(Settings.round));

  const name = withNamespace(ns, "roundId");

  const [roundId, setRoundId] = useFieldState(name);

  const roundIds: number[] = useSelector(getCurrentRoundsList) || [];
  const roundByIdLookup: { key: SaleRound } = useSelector(getRounds) || {};
  const saleRoundOptions: SaleRound[] = roundIds.map(id => roundByIdLookup[id]);

  const options: RoundOption[] = saleRoundOptions.map(saleRound => ({
    label: saleRound.name,
    value: saleRound.id,
  }));

  const onChange = useCallback(
    (newRoundId: number) => {
      setRoundId(newRoundId);
      if (typeof onAfterChanged === "function") {
        onAfterChanged(newRoundId);
      }
    },
    [onAfterChanged, setRoundId],
  );

  const onSelect = useCallback(
    (selectedOption: RoundOption) => {
      onChange(selectedOption.value);
    },
    [onChange],
  );

  useEffect(() => {
    if (!roundId && globalRoundId) {
      onChange(globalRoundId);
    }
  }, [globalRoundId, onChange, roundId]);

  return options.length <= 3 ? (
    <OptionToggler
      onChange={onChange}
      options={options}
      value={roundId}
      name={name}
    />
  ) : (
    <ReactSelect
      options={options}
      onChange={onSelect}
      value={options.find(v => v.value === roundId)}
      menuPortalTarget={document.body}
    />
  );
}

type Type<T> = T;

type FieldState<T> = Type<[T, (newValue: T) => void]>;

export function PenRangeFilterForm(
  props: FilterComponentProps,
): React.JSX.Element {
  const {
    namespace: ns,
    id,
    areEmptyPensVisible = false,
    forMlaTabs = false,
  } = props;

  const [startPenId, setStartPenId] = useFieldState(
    withNamespace(ns, "startPenId"),
  ) as FieldState<string | null>;
  const [endPenId, setEndPenId] = useFieldState(
    withNamespace(ns, "endPenId"),
  ) as FieldState<string | null>;
  const [roundId, setRoundId] = useFieldState(
    withNamespace(ns, "roundId"),
  ) as FieldState<number | null>;

  const changeRoundId = useCallback(
    (newRoundId: number) => {
      setStartPenId(null);
      setEndPenId(null);
      setRoundId(newRoundId);
    },
    [setEndPenId, setStartPenId, setRoundId],
  );

  function onAfterRoundChanged(newRoundId: number | null) {
    if (roundId !== newRoundId) {
      changeRoundId(newRoundId);
    }
  }

  return (
    <>
      <ControlGroupHeader
        title="Pen Range"
        subtitle="Range based on selling order."
      />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <RoundSelector namespace={ns} onAfterChanged={onAfterRoundChanged} />
        </Grid>
        <Grid item xs={6}>
          <PenPicker
            areEmptyPensVisible={areEmptyPensVisible}
            isCreateEnabled={false}
            isCreateFreeFormEnabled={false}
            isCreateArchetypeDerivedEnabled={false}
            enableArchetypes={false}
            required
            label="Start Pen"
            name="startPen"
            idName={withNamespace(ns, "startPenId")}
            dataName="startPenData"
            penType={PenTypes.SELLING}
            penFilters={{ saleRoundId: roundId }}
          />
        </Grid>

        <Grid item xs={6}>
          <PenPicker
            areEmptyPensVisible={areEmptyPensVisible}
            isCreateEnabled={false}
            isCreateFreeFormEnabled={false}
            isCreateArchetypeDerivedEnabled={false}
            enableArchetypes={false}
            required
            label="End Pen"
            name="endPen"
            idName={withNamespace(ns, "endPenId")}
            dataName="endPenData"
            penType={PenTypes.SELLING}
            penFilters={{ saleRoundId: roundId }}
          />
        </Grid>
        {/* TODO: remove forMlaTabs and accomdate for MLA pens in range in AuctionPenSpanMessage */}
        {roundId && !forMlaTabs && (
          <Grid item xs={12}>
            <AuctionPenSpanMessage
              id={id}
              startPenId={startPenId}
              endPenId={endPenId}
              roundId={roundId}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
}
